/* global google */
import {useEffect, useContext, cloneElement} from 'react';
import PropTypes from 'prop-types';
import {EDAP_EVENTS, triggerEdapEventWithContext} from '../../utils/edapHelper';
import {TrackingContext} from '@homeaway/react-event-tracking';
import {useMapContext} from '../../context/MapContext';
const CustomMarker = (props) => {
    const context = useContext(TrackingContext);
    const mapContext = useMapContext();
    const {map, position, markerContent, className} = props;
    let customMarker;
    let positionInstance;
    let listeners;
    const onAdd = () => {
        customMarker.getPanes().overlayImage.appendChild(customMarker.anchor);
    };

    const onRemove = () => {
        if (customMarker.anchor.parentElement) {
            customMarker.anchor.parentElement.removeChild(customMarker.anchor);
        }
        listeners.map((listener) => {
            google.maps.event.removeListener(listener);
        });
    };

    const getPosition = () => {
        return positionInstance;
    };

    const getDraggable = () => {
        return false;
    };

    const callback = (event) => {
        const {onClick, pinType} = props;
        event.stopPropagation();
        event.preventDefault();
        triggerEdapEventWithContext(context, EDAP_EVENTS.MAP_PIN_SELECTED, {
            action_location: mapContext.appName,
            pin_type: pinType,
        });
        onClick();
    };

    const draw = () => {
        const divPosition = customMarker
            .getProjection()
            .fromLatLngToDivPixel(positionInstance);

        // Hide the customMarker when it is far out of view.
        const display =
            Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000
                ? 'block'
                : 'none';

        if (display === 'block') {
            customMarker.anchor.style.left = `${divPosition.x}px`;
            customMarker.anchor.style.top = `${divPosition.y}px`;
        }
        if (customMarker.anchor.style.display !== display) {
            customMarker.anchor.style.display = display;
        }
    };

    positionInstance =
        position instanceof google.maps.LatLng
            ? position
            : new google.maps.LatLng(position.lat, position.lng);
    customMarker = new google.maps.OverlayView();
    customMarker.anchor = document.createElement('div');
    customMarker.anchor.classList.add('custom-marker');
    customMarker.anchor.classList.add(className);
    customMarker.anchor.innerHTML = markerContent;
    customMarker.onAdd = onAdd;
    customMarker.onRemove = onRemove;
    customMarker.getPosition = getPosition;
    customMarker.getDraggable = getDraggable;
    customMarker.draw = draw;
    customMarker.setMap(map);
    listeners = [
        customMarker.anchor.addEventListener('click', callback),
        customMarker.anchor.addEventListener('touchstart', callback),
    ];

    useEffect(() => {
        return () => {
            /* Remove all marker listeners */
            google.maps.event.clearInstanceListeners(customMarker);
            /* Remove marker */
            customMarker.setMap(null);
        };
    });

    const renderMarker = () => {
        const {children, isHighlighted} = props;
        customMarker.anchor.innerHTML = markerContent;

        // z-index needs to be inline style
        if (isHighlighted) {
            customMarker.anchor.style.zIndex = 999;
        } else {
            customMarker.anchor.style.zIndex = 510;
        }
        if (children) {
            return cloneElement(children, {
                map,
                marker: customMarker,
            });
        }
        return null;
    };

    return renderMarker();
};

CustomMarker.propTypes = {
    /** Single child to be rendered under marker */
    children: PropTypes.node,
    /** Unique marker identifier */
    id: PropTypes.string.isRequired,
    /** Callback for clicking on the marker */
    onClick: PropTypes.func,
    /** Callback for hovering on the marker */
    onHover: PropTypes.func,
    /**
     * Callback for after you finish hovering. // TODO figure out why its not working
     */
    onHoverLeave: PropTypes.func,
    /** map reference (automatically passed down if wrapped by GoogleMaps) */
    map: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    /** lat and lng object describing the origin of the marker*/
    position: PropTypes.shape({
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired,
    }).isRequired,
    label: PropTypes.string,
    isHighlighted: PropTypes.bool,
    markerContent: PropTypes.string,
    className: PropTypes.string,
    pinType: PropTypes.string,
};

CustomMarker.defaultProps = {
    onClick: /* istanbul ignore next */ () => {},
    onHover: /* istanbul ignore next */ () => {},
    onHoverLeave: /* istanbul ignore next */ () => {},
    label: '',
    isHighlighted: false,
};
export default CustomMarker;
